import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FaRobot,
  FaCloud,
  FaCode,
  FaMailBulk,
  FaMicrochip,
  FaCube
} from 'react-icons/fa';
import ContactForm from '../components/ContactForm';

const SimpleHomePage = () => {
  const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'info@accurati.io';
  const navigate = useNavigate();

  // Hero title
  const heroPhrase = {
    part1: 'Software engineering',
    part2: 'and technical consulting.'
  };

  // Animation on scroll effect
  useEffect(() => {
    const handleScrollAnimation = () => {
      const scrollElements = document.querySelectorAll('.animate-on-scroll');

      scrollElements.forEach(element => {
        const elementPosition = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;

        if (elementPosition < windowHeight - 100) {
          element.classList.add('animate');
        }
      });
    };

    window.addEventListener('scroll', handleScrollAnimation);
    handleScrollAnimation(); // Initial check

    return () => window.removeEventListener('scroll', handleScrollAnimation);
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-overlay"></div>
        <Container>
          <Row className="align-items-center">
            <Col md={7} className="hero-content">
              <h1 className="hero-title animate-on-scroll">
                <span className="highlight">{heroPhrase.part1}</span> {heroPhrase.part2}
              </h1>
              <div className="hero-subtitle animate-on-scroll">
                Based in Ireland, serving EU and international clients with tailored software solutions,
                cloud infrastructure, and embedded/IoT systems expertise.
              </div>
              <div className="hero-buttons animate-on-scroll">
                <Button
                  className="btn-primary me-3"
                  onClick={() => {
                    const services = document.getElementById('services');
                    if (services) {
                      window.scrollTo({
                        top: services.offsetTop - 80,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  Our Services
                </Button>
                <Button
                  className="btn-outline"
                  onClick={() => {
                    const contact = document.getElementById('contact');
                    if (contact) {
                      window.scrollTo({
                        top: contact.offsetTop - 80,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  Get in Touch
                </Button>
              </div>
            </Col>
            <Col md={5} className="hero-image-container">
              <div className="hero-image animate-on-scroll">
                <div className="tech-circle"></div>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="about-image animate-on-scroll">
                <img
                  src="/images/about.png"
                  alt="Software Development"
                  className="img-fluid rounded shadow"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="about-content animate-on-scroll">
                <h2>What We Do</h2>
                <p className="lead">
                  Accurati provides tailored software engineering and technical consulting services for enterprise applications,
                  cloud infrastructure, and embedded/IoT systems.
                </p>
                <p>
                  We work with clients on backend systems, cloud infrastructure, and embedded/IoT projects.
                  Technologies include Python/C++, computer vision and AI on NVIDIA platform, and UWB/FiRa 3.0 stack.
                </p>
                {/*<p>*/}
                {/*  Our expertise includes complex algorithms, computer vision, embedded systems, cloud infrastructure, and full-stack development*/}
                {/*  using Python, C++, Java, Node.js, AWS, Kubernetes, and modern DevOps practices.*/}
                {/*</p>*/}
                <Button
                  className="btn-outline mt-4"
                  onClick={() => {
                    const services = document.getElementById('services');
                    if (services) {
                      window.scrollTo({
                        top: services.offsetTop - 80,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  View Services
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>


      {/* Services Section */}
      <section id="services" className="services-section">
        <Container>
          <div className="text-center mb-5 animate-on-scroll">
            <h2>Services</h2>
            <p className="lead">
              Tailored software engineering expertise across backend systems, cloud infrastructure, and embedded/IoT
            </p>
          </div>

          <Row>
            <Col lg={3} md={6} className="mb-4">
              <div className="service-card animate-on-scroll">
                <div className="service-icon">
                  <FaCode />
                </div>
                <h3>Custom Software</h3>
                <p>Backend systems, API development, microservices, and data processing applications using Python, Java, Node.js, and C++</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="service-card animate-on-scroll" style={{animationDelay: '0.2s'}}>
                <div className="service-icon">
                  <FaCloud />
                </div>
                <h3>Cloud & DevOps</h3>
                <p>AWS infrastructure, Docker/Kubernetes deployments, CI/CD automation, and infrastructure as code</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="service-card animate-on-scroll" style={{animationDelay: '0.4s'}}>
                <div className="service-icon">
                  <FaMicrochip />
                </div>
                <h3>Embedded/IoT Systems</h3>
                <p>Firmware and application development for edge computing, computer vision, and wireless connectivity (FiRa 3.0/UWB, BLE, Zigbee)</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="service-card animate-on-scroll" style={{animationDelay: '0.6s'}}>
                <div className="service-icon">
                  <FaRobot />
                </div>
                <h3>Computer Vision</h3>
                <p>Image processing and pattern recognition on embedded platforms, including NVIDIA Jetson edge deployments</p>
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg={3} md={6} className="mb-4 mx-auto">
              <div className="service-card animate-on-scroll clickable" style={{animationDelay: '0.8s'}} onClick={() => navigate('/product-development')}>
                <div className="service-icon">
                  <FaCube />
                </div>
                <h3>Product Development</h3>
                <p>3D printing service and CAD modeling for hardware prototypes (Ireland/EU delivery)</p>
                <div className="learn-more">Learn More →</div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <div className="cta-card animate-on-scroll">
            <Row className="align-items-center">
              <Col lg={8}>
                <h2>Work With Us</h2>
                <p className="lead mb-0">
                  Looking for experienced software engineers for your next project?
                  We work on contract basis for custom software development, cloud infrastructure, backend systems, and embedded/IoT projects.
                </p>
              </Col>
              <Col lg={4} className="text-lg-end mt-4 mt-lg-0">
                <Button
                  className="btn-primary"
                  onClick={() => {
                    const contact = document.getElementById('contact');
                    if (contact) {
                      window.scrollTo({
                        top: contact.offsetTop - 80,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  Get in Touch
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <Container>
          <div className="text-center mb-5 animate-on-scroll">
            <h2>Let's Connect</h2>
            <p className="lead">
              Get in touch to discuss your project requirements
            </p>
          </div>
          <Row>
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="contact-info animate-on-scroll">
                <p>
                  Discuss your technical requirements with us.
                  Fill out the form and we'll respond within 1-2 business days.
                </p>

                <div className="contact-details">
                  <div className="contact-item">
                    <h4><FaMailBulk/>&nbsp;Email</h4>
                    <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="contact-form-wrapper animate-on-scroll">
                <ContactForm />
              </div>
              &nbsp;
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default SimpleHomePage;
